import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import { ProductCategoryService } from '../../services/product-category.service';
import { ProductService } from '../../index';
import {
  BehaviorSubject,
  pipe,
  Subject,
  switchMap,
  withLatestFrom
} from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { RetailProductService } from '../../services/retail-product.service';
import chroma from 'chroma-js';

type CategoryGridItemType =
  | 'parentCategory'
  | 'category'
  | 'item'
  | 'product'
  | 'retailProduct';

export interface CategoryGridItem {
  id: number;
  label: string;
  type: CategoryGridItemType;
  children: CategoryGridItem[];
  background: string;
  contrast: string;
  outline: string;
  enabled?: boolean;
}

interface CategoryGridState {
  breadcrumbs: CategoryGridItem[];
  productCache: { [categoryId: number]: CategoryGridItem[] };
  itemCache: { [productId: number]: CategoryGridItem[] };
  categoryCache: CategoryGridItem[];
  lastSelectedItem: CategoryGridItem | null | undefined;
  productType: 'hire' | 'retail';
  loading: boolean;
}

const initialState: CategoryGridState = {
  lastSelectedItem: undefined,
  breadcrumbs: [],
  productCache: {},
  categoryCache: [],
  productType: 'hire',
  itemCache: {},
  loading: true
};

@Injectable()
export class CategoryGridStoreService extends ComponentStore<CategoryGridState> {
  private loadingSource = new BehaviorSubject(false);

  constructor(
    private categoryService: ProductCategoryService,
    private productService: ProductService,
    private retailProductService: RetailProductService,
    private toastService: ToastrService
  ) {
    super(initialState);
  }

  readonly loading$ = this.loadingSource.asObservable();
  private itemSelected = new Subject<CategoryGridItem>();

  onItemSelected$ = this.itemSelected.asObservable();

  readonly setProductType = this.updater<'hire' | 'retail'>(
    (state, productType) => ({
      ...state,
      productType
    })
  );

  private readonly productType$ = this.select((state) => state.productType);

  readonly getAndCacheCategories = this.effect<void>((_) =>
    this.productType$.pipe(
      tap(() => this.loadingSource.next(true)),
      switchMap((type) =>
        this.categoryService.getCategoryGrid(type).pipe(
          map((categories) =>
            categories.map((category) => ({
              ...category,
              background: category.color ? category.color : DEFAULT_BACKGROUND,
              contrast: this.getContrastColor(category.color),
              outline: this.getOutlineColor(category.color)
            }))
          )
        )
      ),
      tapResponse(
        (categories: CategoryGridItem[]) => this.setCategoryCache(categories),
        () =>
          this.toastService.error(
            'Please try again',
            'Unable to fetch categories'
          )
      ),
      tap(() => this.loadingSource.next(false))
    )
  );

  private readonly categories$ = this.select((state) => {
    return state.categoryCache.length > 0;
  }).pipe(
    mergeMap((exists) => {
      if (!exists) {
        this.getAndCacheCategories();
      }
      return this.state$.pipe(map(({ categoryCache }) => categoryCache));
    })
  );

  readonly breadcrumb$ = this.select(({ breadcrumbs }) => breadcrumbs);

  readonly selectGridItem = this.effect<CategoryGridItem>(
    pipe(
      withLatestFrom(this.breadcrumb$),
      tap(([item, breadcrumbs]) => {
        console.log({ item });
        if (!item.enabled) {
          return;
        }
        if (item.type === 'item' || item.type === 'retailProduct') {
          this.itemSelected.next(item);
          return;
        }
        this.patchState({
          breadcrumbs: breadcrumbs.concat([item]),
          lastSelectedItem: item
        });
      })
    )
  );

  readonly jumpToBreadcrumb = this.effect<CategoryGridItem | null>(
    pipe(
      withLatestFrom(this.breadcrumb$),
      tap(([item, breadcrumbs]) => {
        this.patchState({
          breadcrumbs:
            item === null
              ? []
              : breadcrumbs.slice(0, breadcrumbs.indexOf(item) + 1),
          lastSelectedItem: item
        });
      })
    )
  );

  readonly jumpToParent = this.effect<void>(
    pipe(
      withLatestFrom(this.breadcrumb$),
      tap(([_, breadcrumbs]) => {
        this.patchState({
          breadcrumbs: breadcrumbs.slice(0, breadcrumbs.length - 1),
          lastSelectedItem: breadcrumbs[breadcrumbs.length - 2]
        });
      })
    )
  );

  readonly items$ = this.select(
    ({
      lastSelectedItem,
      categoryCache,
      productCache,
      itemCache,
      productType
    }) => {
      console.log({ lastSelectedItem });
      if (!lastSelectedItem) {
        return categoryCache;
      }
      if (lastSelectedItem.children.length) {
        return lastSelectedItem.children;
      }
      if (lastSelectedItem.type === 'category') {
        if (!productCache[lastSelectedItem.id]) {
          this.getAndCacheCategoryProducts({
            categoryId: lastSelectedItem.id,
            type: productType
          });
          return [];
        }
        return productCache[lastSelectedItem.id];
      }
      if (lastSelectedItem.type === 'product') {
        if (!itemCache[lastSelectedItem.id]) {
          this.getAndCacheProductItems(lastSelectedItem.id);
          return [];
        }
        return itemCache[lastSelectedItem.id];
      }
    }
  ).pipe(map((items) => items.sort((a, b) => a.label.localeCompare(b.label))));

  private readonly getAndCacheCategoryProducts = this.effect<{
    categoryId: number;
    type: 'hire' | 'retail';
  }>(
    pipe(
      tap(() => this.loadingSource.next(true)),
      switchMap(({ categoryId, type }) => {
        if (type === 'hire') {
          return this.loadHireProducts(categoryId);
        }
        if (type === 'retail') {
          return this.loadRetailProducts(categoryId);
        }
      }),
      tap(() => this.loadingSource.next(false))
    )
  );

  private readonly getAndCacheProductItems = this.effect<number>(
    pipe(
      tap(() => this.loadingSource.next(true)),
      switchMap((productId) => {
        return this.productService.getRelatedItems(productId).pipe(
          map((items) =>
            items.map((itemModel) => ({
              id: itemModel.id,
              label: itemModel.code,
              children: [],
              type: 'item' as CategoryGridItemType,
              enabled: itemModel.status === 'Available',
              background: DEFAULT_BACKGROUND,
              contrast: DEFAULT_CONTRAST,
              outline: DEFAULT_CONTRAST
            }))
          ),
          tapResponse(
            (items) => this.cacheItems({ items, productId }),
            (error) =>
              this.toastService.error(
                'Could not fetch items related to this products, please try again'
              )
          )
        );
      }),
      tap(() => this.loadingSource.next(false))
    )
  );

  private readonly setCategoryCache = this.updater<CategoryGridItem[]>(
    (state, categoryCache) => ({
      ...state,
      categoryCache
    })
  );

  private readonly cacheProducts = this.updater<{
    categoryId: number;
    products: CategoryGridItem[];
  }>((state, { categoryId, products }) => ({
    ...state,
    productCache: { ...state.productCache, [categoryId]: products }
  }));

  private readonly cacheItems = this.updater<{
    productId: number;
    items: CategoryGridItem[];
  }>((state, { productId, items }) => ({
    ...state,
    itemCache: { ...state.itemCache, [productId]: items }
  }));

  private loadHireProducts(categoryId: number) {
    return this.categoryService
      .getProductCategory(categoryId, ['products', 'subCategories'])
      .pipe(
        map(({ products, sub_categories }) => {
          return sub_categories
            .map((category) => {
              return {
                id: category.id,
                label: category.name,
                children: [],
                type: 'category' as CategoryGridItemType,
                background: category.color
                  ? category.color
                  : DEFAULT_BACKGROUND,
                contrast: this.getContrastColor(category.color),
                outline: this.getOutlineColor(category.color)
              };
            })
            .concat(
              products.map((product) => {
                return {
                  id: product.id,
                  label: product.name,
                  children: [],
                  type: 'product' as CategoryGridItemType,
                  background: DEFAULT_BACKGROUND,
                  contrast: DEFAULT_CONTRAST,
                  outline: DEFAULT_CONTRAST
                };
              })
            );
        }),
        tapResponse(
          (products) => {
            this.cacheProducts({ categoryId, products });
          },
          (error) =>
            this.toastService.error(
              'Could not get products',
              'Please try again'
            ) && console.log(error)
        )
      );
  }

  private loadRetailProducts(categoryId: number) {
    return this.retailProductService.getForCategory([categoryId]).pipe(
      map((results) => {
        return results.flatMap(({ children, products }) => {
          return [
            ...children.map((child) => ({
              id: child.id,
              label: child.name,
              children: [],
              type: 'category' as CategoryGridItemType,
              background: child.color ? child.color : DEFAULT_BACKGROUND,
              contrast: this.getContrastColor(child.color),
              outline: this.getOutlineColor(child.color)
            })),
            ...products.map((product) => ({
              id: product.id,
              label: product.name,
              children: [],
              type: 'retailProduct' as CategoryGridItemType,
              background: DEFAULT_BACKGROUND,
              outline: DEFAULT_CONTRAST,
              contrast: DEFAULT_CONTRAST
            }))
          ];
        });
      }),
      tapResponse(
        (products) => {
          this.cacheProducts({ categoryId, products });
        },
        (error) =>
          this.toastService.error('Could not get products', 'Please try again')
      )
    );
  }

  private getContrastColor(color: string) {
    if (!color) {
      return DEFAULT_CONTRAST;
    }
    return chroma(color).luminance() > 0.5 ? '#000' : '#fff';
  }

  private getOutlineColor(color: string) {
    if (!color) {
      return DEFAULT_CONTRAST;
    }
    const c = chroma(color);
    return c.luminance() > 0.8 ? c.brighten(0.2).hex() : c.darken(0.2).hex();
  }
}

const DEFAULT_BACKGROUND = '#eff6ff';
const DEFAULT_CONTRAST = '#0067ec';
